import { useState } from 'react'
import { NAV_GROUPS, HOME } from '@/routes/navigation'
import { Icon } from './Icon'
import { VARIANT_COLOR } from './launcherCardVariant'

/**
 * `aside.sidebar` → `sidebar` (process/08-COMPONENT-CATALOGUE.md). "The primary navigation
 * region." §27.1's six groups, closed, in the fixed order the design table gives them.
 * `LLD-SPA-023`: adding a group is a register change, never a hand-edit here beyond the register
 * this file already reads (`src/routes/navigation.ts`).
 *
 * **Rebuilt W175** — every gap found by comparing directly against Prototype_v2.html's own
 * `aside.sidebar` (lines 948-990), not assumed from the shape this file already had:
 *
 * 1. **No icon anywhere.** The prototype draws one filled (`f-*`) icon per group (`.nav-item.top`)
 *    and one outline (`i-*`) icon per leaf (`.nav-leaf`), both coloured to the group's own accent —
 *    completely absent before this pass. `NavGroup.icon`/`NavLeaf.icon` (the latter already added at
 *    W174 for `LauncherCard`) are the source; `Icon.tsx` itself had a real bug blocking this (fixed
 *    the same pass, see its own doc comment) — none of these seven `f-*` icons would have rendered
 *    filled otherwise.
 * 2. **`data-cmp` was backwards.** The catalogue maps `div.nav-item.top` → `nav-group` and
 *    `div.nav-leaf` → `nav-item` — counter-intuitive, but that is what is catalogued. The previous
 *    build tagged the wrapping `<div>` around each group `nav-group` and the header BUTTON inside it
 *    (the actual `.nav-item.top` element) `nav-item` — exactly inverted. Now the header carries
 *    `nav-group`, each leaf carries `nav-item`, and the wrapping `<div>` carries neither (the
 *    prototype has no such wrapper at all; these are flat siblings).
 * 3. **No disclosure state.** `.nav-sub{display:none}` / `.nav-sub.open{display:block}`, toggled by
 *    `tg()`, which also flips each group's own chevron between `i-chevU` (open) and `i-chevD`
 *    (closed) — the previous build always rendered every leaf with no chevron and no way to collapse
 *    a group at all. Every group starts open, matching the prototype's own default markup
 *    (`.nav-sub.open` on all six from the start).
 * 4. **`.on` (current route) was an approximation.** `.nav-leaf.on{background:var(--sel);
 *    color:var(--blue);font-weight:600;box-shadow:inset 3px 0 0 var(--blue)}` — the previous build
 *    only ever changed the text colour. Now applied in full, plus the identical treatment
 *    `.nav-item.on` gives Home (no colour change there, just the background/box-shadow — Home has
 *    no group colour of its own).
 * 5. **One deliberate departure from the prototype's own wiring, reported rather than silently
 *    picked.** The prototype's group header only ever calls `tg(this)` (toggle open/closed) for
 *    five of the six groups; only `Account`'s own title carries a nested `event.stopPropagation();
 *    go('secaccount')`, making it the one group whose header text is clickable to its own landing
 *    page. `design/27-ui-surface-and-navigation.md` §27.1 property 1 states all SIX groups equally
 *    have a landing page (already built, routed and tested — `GroupLanding.tsx`), with nothing
 *    distinguishing Account from the other five — this asymmetry reads as an incomplete mockup
 *    wiring rather than a real decision, so every group's own title here is clickable to its
 *    landing page, matching `Account`'s own already-correct pattern, with the rest of the row
 *    (icon, padding, chevron) still toggling the disclosure.
 *
 * **`collapsed` (new) — an icon-only rail, the modern collapse pattern reported live** ("just like
 * you'd have in a modern app"), not something the prototype draws at all (it has no collapsed
 * state; §24.2c treats this as new product surface, not a value the prototype owns). Collapsed, a
 * group header navigates straight to its own landing page on click rather than toggling disclosure
 * — there is no room to show leaves at `w-16`, so the accordion behaviour itself is suspended
 * rather than rendered unusably small. `collapsed`/`onToggleCollapse` are owned by `ShellLayer.tsx`,
 * not local state here, because `main`'s own left offset has to move in lock-step with this rail's
 * width and neither component can dictate the other's layout from inside a sibling. Not persisted
 * (`ui-contract/no-browser-storage` forbids `localStorage`, `LLD-SPA-068`) — resets to expanded on
 * a fresh load, same as the rest of the shell's own transient state.
 *
 * **The toggle is a single `f-grid` ("9 dots") icon pinned above `Home`, in both states** — asked
 * for directly ("mimic the behaviour of MUI... 9 dots icon at the top when collapsed"), after an
 * earlier chevron-at-the-bottom version. One icon does both directions (click to collapse, click
 * again to expand) rather than a chevron that flips — the `title` attribute is what carries the
 * current direction, the same way a disclosure chevron's state is read from its own rotation, not a
 * second visible label.
 */
export function Sidebar({
  currentRouteId,
  onNavigate,
  collapsed,
  onToggleCollapse,
}: {
  currentRouteId: string
  onNavigate: (routeId: string) => void
  collapsed: boolean
  onToggleCollapse: () => void
}) {
  const [openGroups, setOpenGroups] = useState<ReadonlySet<string>>(() => new Set(NAV_GROUPS.map((g) => g.routeId)))

  return (
    <aside data-cmp="sidebar" className={`fixed left-0 top-10 bottom-0 ${collapsed ? 'w-16' : 'w-[225px]'} bg-panel border-r border-bd flex flex-col`}>
      <button
        type="button"
        className={`flex items-center w-full text-left border-b border-bd py-4 text-muted hover:bg-row hover:text-tx ${collapsed ? 'justify-center' : 'gap-3 px-6'}`}
        onClick={onToggleCollapse}
        title={collapsed ? 'Expand navigation' : 'Collapse navigation'}
      >
        <Icon name="f-grid" size="s" />
        {!collapsed && <span className="text-small font-semibold">Collapse</span>}
      </button>
      <div className="flex-1 overflow-y-auto overflow-x-hidden">
        <button
          data-cmp="nav-group"
          className={`flex items-center w-full text-left py-5 text-body-lg font-semibold ${collapsed ? 'justify-center' : 'gap-3 px-6'} ${
            currentRouteId === HOME.routeId ? 'bg-sel shadow-[inset_3px_0_0_var(--blue)]' : ''
          }`}
          onClick={() => onNavigate(HOME.routeId)}
          title={collapsed ? HOME.title : undefined}
        >
          <Icon name={HOME.icon} color="blue" />
          {!collapsed && HOME.title}
        </button>

        {NAV_GROUPS.map((group) => {
          const isOpen = openGroups.has(group.routeId)
          return (
            <div key={group.routeId} className="border-t border-bd">
              <button
                data-cmp="nav-group"
                className={`flex items-center w-full text-left py-5 text-body font-semibold text-tx ${collapsed ? 'justify-center' : 'gap-3 px-6'}`}
                onClick={() =>
                  collapsed
                    ? onNavigate(group.routeId)
                    : setOpenGroups((prev) => {
                        const next = new Set(prev)
                        if (next.has(group.routeId)) next.delete(group.routeId)
                        else next.add(group.routeId)
                        return next
                      })
                }
                title={collapsed ? group.title : undefined}
              >
                <Icon name={group.icon} color={VARIANT_COLOR[group.accent]} />
                {!collapsed && (
                  <>
                    <span className="flex-1" onClick={(e) => { e.stopPropagation(); onNavigate(group.routeId) }}>
                      {group.title}
                    </span>
                    <Icon name={isOpen ? 'i-chevU' : 'i-chevD'} size="xs" color="muted" />
                  </>
                )}
              </button>
              {!collapsed && isOpen && (
                <div data-cmp="nav-subnav">
                  {group.leaves.map((leaf) => {
                    const active = currentRouteId === leaf.routeId
                    return (
                      <button
                        key={leaf.routeId}
                        data-cmp="nav-item"
                        className={`flex items-center gap-3 w-full text-left pl-9 pr-6 py-3 text-body ${
                          active ? 'bg-sel text-blue font-semibold shadow-[inset_3px_0_0_var(--blue)]' : 'text-tx2'
                        }`}
                        onClick={() => onNavigate(leaf.routeId)}
                      >
                        <Icon name={leaf.icon} size="s" color={VARIANT_COLOR[group.accent]} />
                        {leaf.title}
                      </button>
                    )
                  })}
                </div>
              )}
            </div>
          )
        })}
      </div>
    </aside>
  )
}
